import React, { useEffect, useState, useCallback } from 'react';
import Header from '../../components/layout/Header';
import { apiFetch } from '../../utils/api';
import { useToast } from '../../context/ToastContext';
import {
  Shield, Search, RotateCcw, ChevronLeft, ChevronRight,
  AlertTriangle, Eye, Download, Phone, Mail, Loader2,
} from 'lucide-react';
import './SecurityLogs.css';

const PAGE_SIZE = 50;

const ACTION_OPTIONS = [
  { value: '', label: 'All actions' },
  { value: 'phone_reveal', label: 'Phone reveal' },
  { value: 'email_reveal', label: 'Email reveal' },
  { value: 'export', label: 'Export' },
  { value: 'view', label: 'Lead view' },
  { value: 'view_list', label: 'List view' },
  { value: 'call', label: 'Call' },
  { value: 'update', label: 'Edit' },
  { value: 'status_change', label: 'Status change' },
  { value: 'assign', label: 'Assign' },
  { value: 'delete', label: 'Delete' },
  { value: 'import', label: 'Import' },
  { value: 'alert', label: 'Security alert' },
];

// action -> { label, className, icon }
const ACTION_META = {
  phone_reveal: { label: 'Phone reveal', cls: 'sec-tag--reveal', Icon: Phone },
  email_reveal: { label: 'Email reveal', cls: 'sec-tag--reveal', Icon: Mail },
  export: { label: 'Export', cls: 'sec-tag--danger', Icon: Download },
  view: { label: 'View', cls: 'sec-tag--muted', Icon: Eye },
  view_list: { label: 'List view', cls: 'sec-tag--muted', Icon: Eye },
  alert: { label: 'Alert', cls: 'sec-tag--danger', Icon: AlertTriangle },
};

const todayStr = () => new Date().toISOString().slice(0, 10);
const daysAgoStr = (n) => new Date(Date.now() - n * 86400000).toISOString().slice(0, 10);

const fmtTime = (s) => {
  if (!s) return '—';
  const d = new Date(s.replace(' ', 'T'));
  if (isNaN(d)) return s;
  return d.toLocaleString();
};

const renderDetails = (details) => {
  if (!details) return '—';
  let d = details;
  if (typeof d === 'string') { try { d = JSON.parse(d); } catch { return d; } }
  if (d.changes) {
    return Object.entries(d.changes)
      .map(([k, v]) => `${k}: ${v.from ?? '∅'} → ${v.to ?? '∅'}`)
      .join('; ');
  }
  const parts = [];
  if (d.format) parts.push(`${d.format.toUpperCase()} ×${d.count ?? '?'}`);
  if (d.report) parts.push(d.report);
  if (d.via) parts.push(`via ${d.via}`);
  if (d.to) parts.push(`→ ${d.to}`);
  if (d.name) parts.push(d.name);
  if (d.returned != null) parts.push(`${d.returned} rows`);
  if (d.filters && Object.keys(d.filters).length) parts.push(`filters: ${Object.keys(d.filters).join(', ')}`);
  if (d.reveals != null || d.exports != null) parts.push(`${d.reveals || 0} reveals, ${d.exports || 0} exports`);
  return parts.length ? parts.join(' · ') : JSON.stringify(d);
};

const SecurityLogs = () => {
  const { showToast } = useToast();

  const [stats, setStats] = useState(null);
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);

  // Filters
  const [action, setAction] = useState('');
  const [from, setFrom] = useState(daysAgoStr(7));
  const [to, setTo] = useState(todayStr());
  const [search, setSearch] = useState('');

  const loadStats = useCallback(async () => {
    try {
      const sp = new URLSearchParams();
      if (from) sp.set('from', from);
      if (to) sp.set('to', `${to} 23:59:59`);
      const res = await apiFetch(`audit/stats?${sp.toString()}`);
      if (res.success) setStats(res.data);
    } catch { /* non-fatal */ }
  }, [from, to]);

  const loadLogs = useCallback(async (p = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: p, limit: PAGE_SIZE });
      if (action) params.set('action', action);
      if (from) params.set('from', from);
      if (to) params.set('to', `${to} 23:59:59`);
      if (search.trim()) params.set('search', search.trim());
      const res = await apiFetch(`audit?${params.toString()}`);
      if (res.success) {
        setLogs(res.data || []);
        setTotal(res.pagination?.total || 0);
        setPage(res.pagination?.page || p);
      } else {
        showToast(res.message || 'Failed to load logs', 'error');
      }
    } catch {
      showToast('Failed to load audit logs', 'error');
    }
    setLoading(false);
  }, [action, from, to, search, showToast]);

  useEffect(() => {
    loadStats();
    loadLogs(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyFilters = () => { loadStats(); loadLogs(1); };
  const resetFilters = () => {
    setAction(''); setFrom(daysAgoStr(7)); setTo(todayStr()); setSearch('');
    setTimeout(() => { loadStats(); loadLogs(1); }, 0);
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div>
      <Header title="Security & Audit" subtitle="Who accessed, revealed, exported, or changed lead data" />
      <div className="page">

        {/* Suspicious / top users */}
        {stats && (
          <div className="sec-cards">
            <div className="sec-card sec-card--watch">
              <div className="sec-card__head">
                <AlertTriangle size={16} /> Top reveal / export users
                <span className="sec-card__hint">in selected range</span>
              </div>
              <div className="sec-card__body">
                {(stats.perUser || []).slice(0, 6).map((u) => {
                  const flagged = (Number(u.reveals) || 0) >= 50 || (Number(u.exports) || 0) >= 3;
                  return (
                    <div key={u.u_id || u.username} className={`sec-userrow ${flagged ? 'sec-userrow--flag' : ''}`}>
                      <span className="sec-userrow__name">{u.username || `User #${u.u_id}`}</span>
                      <span className="sec-userrow__stat"><Phone size={12} /> {u.reveals || 0}</span>
                      <span className="sec-userrow__stat"><Download size={12} /> {u.exports || 0}</span>
                      <span className="sec-userrow__stat sec-userrow__stat--muted"><Eye size={12} /> {u.views || 0}</span>
                    </div>
                  );
                })}
                {(!stats.perUser || stats.perUser.length === 0) && (
                  <div className="sec-empty">No activity in this range.</div>
                )}
              </div>
            </div>

            <div className="sec-card">
              <div className="sec-card__head"><Shield size={16} /> Actions breakdown</div>
              <div className="sec-card__body sec-actions">
                {(stats.byAction || []).map((a) => (
                  <div key={a.action} className="sec-actionpill">
                    <span>{ACTION_META[a.action]?.label || a.action}</span>
                    <strong>{a.cnt}</strong>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="sec-filters">
          <div className="sec-field">
            <label>Action</label>
            <select value={action} onChange={(e) => setAction(e.target.value)}>
              {ACTION_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div className="sec-field">
            <label>From</label>
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          </div>
          <div className="sec-field">
            <label>To</label>
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
          </div>
          <div className="sec-field sec-field--grow">
            <label>Search (user / IP)</label>
            <input
              type="text" value={search} placeholder="username or IP…"
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && applyFilters()}
            />
          </div>
          <div className="sec-filters__actions">
            <button className="sec-btn sec-btn--primary" onClick={applyFilters}><Search size={14} /> Apply</button>
            <button className="sec-btn" onClick={resetFilters}><RotateCcw size={14} /> Reset</button>
          </div>
        </div>

        {/* Logs table */}
        <div className="sec-tablewrap">
          <table className="sec-table">
            <thead>
              <tr>
                <th>Time</th><th>User</th><th>Role</th><th>Action</th>
                <th>Lead</th><th>Details</th><th>IP</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="sec-loading"><Loader2 size={16} className="sec-spin" /> Loading…</td></tr>
              ) : logs.length === 0 ? (
                <tr><td colSpan={7} className="sec-empty">No matching activity.</td></tr>
              ) : logs.map((row) => {
                const meta = ACTION_META[row.action] || { label: row.action, cls: 'sec-tag--muted', Icon: null };
                const Icon = meta.Icon;
                return (
                  <tr key={row.audit_id}>
                    <td className="sec-td-time">{fmtTime(row.created_at)}</td>
                    <td>{row.username || `#${row.u_id}`}</td>
                    <td className="sec-td-muted">{row.role_slug || '—'}</td>
                    <td><span className={`sec-tag ${meta.cls}`}>{Icon && <Icon size={11} />}{meta.label}</span></td>
                    <td className="sec-td-muted">{row.entity_type === 'lead' && row.entity_id ? `#${row.entity_id}` : '—'}</td>
                    <td className="sec-td-details">{renderDetails(row.details)}</td>
                    <td className="sec-td-muted">{row.ip_address || '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination — always shown; controls active only when >1 page */}
        {total > 0 && (
          <div className="sec-pager">
            <span>{total.toLocaleString()} records · page {page} / {totalPages}</span>
            <div className="sec-pager__btns">
              <button disabled={page <= 1 || loading} onClick={() => loadLogs(page - 1)}><ChevronLeft size={15} /></button>
              <button disabled={page >= totalPages || loading} onClick={() => loadLogs(page + 1)}><ChevronRight size={15} /></button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SecurityLogs;
